import { computed, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useTournamentStore } from '../stores/tournament'
import { useStageStore } from '../stores/stage'
import { useRewardsStore } from '../stores/rewards'
import { useLeaderboardStore } from '../stores/leaderboard'

type PickemWsPayload = {
  stage_id?: number
  tournament_id?: number
  reason?: string
  entity_id?: number
  timestamp?: number
}

type UsePickemWebSocketOptions = {
  tournamentId: Ref<number | null | undefined>
  activeStageId: Ref<number | null>
  showLeaderboard: Ref<boolean>
}

export const usePickemWebSocket = ({
  tournamentId,
  activeStageId,
  showLeaderboard
}: UsePickemWebSocketOptions) => {
  const tournamentStore = useTournamentStore()
  const stageStore = useStageStore()
  const rewardsStore = useRewardsStore()
  const leaderboardStore = useLeaderboardStore()
  const { lastSource, isFinished } = storeToRefs(tournamentStore)

  const tournamentChannel = computed(() => {
    if (isFinished.value) return null

    const id = tournamentId.value
    return id ? `pickem_tournament_${id}` : null
  })

  const stageChannel = computed(() => {
    if (isFinished.value) return null

    const id = activeStageId.value
    return id ? `pickem_stage_${id}` : null
  })

  const refetchTournament = () => {
    const source = lastSource.value
    if (source) {
      return tournamentStore.fetchItem(source).catch(() => undefined)
    }

    const id = tournamentStore.tournament?.id
    if (!id) return Promise.resolve()

    return tournamentStore
      .fetchItem({ mode: 'byId', id })
      .catch(() => undefined)
  }

  const refetchStage = (stageId?: number) => {
    const id = stageId ?? activeStageId.value
    if (!id) return Promise.resolve()

    const isRelevant = id === activeStageId.value || stageStore.data?.id === id

    if (!isRelevant) return Promise.resolve()

    return stageStore.fetchItem(id).catch(() => undefined)
  }

  const refetchLeaderboard = (eventTournamentId?: number) => {
    const id =
      eventTournamentId ?? tournamentId.value ?? leaderboardStore.tournamentId
    if (!id) return Promise.resolve()

    const tasks: Promise<unknown>[] = [
      leaderboardStore.fetchMe(id).catch(() => undefined)
    ]

    if (showLeaderboard.value) {
      tasks.push(leaderboardStore.fetchPage().catch(() => undefined))
    }

    return Promise.all(tasks)
  }

  const refetchRewards = (eventTournamentId?: number) => {
    const id =
      eventTournamentId ?? tournamentId.value ?? tournamentStore.tournament?.id
    if (!id) return Promise.resolve()

    return rewardsStore.fetchRewards(id).catch(() => undefined)
  }

  const onStageUpdated = (payload: PickemWsPayload) => {
    refetchTournament()
    refetchStage(payload?.stage_id)
  }

  const onTournamentUpdated = () => {
    refetchTournament()
  }

  const onLeaderboardUpdated = (payload: PickemWsPayload) => {
    refetchLeaderboard(payload?.tournament_id)
  }

  const onMemberStageUnlocked = (payload: PickemWsPayload) => {
    refetchStage(payload?.stage_id)
    refetchTournament()
  }

  const onMemberRewardUnlocked = (payload: PickemWsPayload) => {
    refetchRewards(payload?.tournament_id)
  }

  const onMemberRewardClaimed = (payload: PickemWsPayload) => {
    refetchRewards(payload?.tournament_id)
  }

  const onTournamentActivated = () => {
    refetchTournament()
  }

  const onTournamentFinished = () => {
    refetchTournament()
  }

  const wsActions = {
    'pickem.stage.updated': onStageUpdated,
    'pickem.tournament.updated': onTournamentUpdated,
    'pickem.leaderboard.updated': onLeaderboardUpdated,
    'pickem.tournament.activated': onTournamentActivated,
    'pickem.tournament.finished': onTournamentFinished,
    'pickem.member.stage_unlocked': onMemberStageUnlocked,
    'pickem.member.reward_unlocked': onMemberRewardUnlocked,
    'pickem.member.reward_claimed': onMemberRewardClaimed
  }

  return {
    tournamentChannel,
    stageChannel,
    wsActions,
    refetchTournament
  }
}

export const usePickemGlobalWebSocket = () => {
  const tournamentStore = useTournamentStore()

  const onTournamentActivated = () =>
    tournamentStore.fetchItem({ mode: 'active' }).catch(() => undefined)

  const onTournamentFinished = () =>
    tournamentStore.fetchItem({ mode: 'active' }).catch(() => undefined)

  return {
    wsActions: {
      'pickem.tournament.activated': onTournamentActivated,
      'pickem.tournament.finished': onTournamentFinished
    }
  }
}
